// Estimation de pose : dès le devis (brouillon compris, sans attendre l'acceptation), le même calcul que « Préparer le
// chantier » — temps prévu, combien de personnes / combien de jours, matériel à commander, postes et étapes. Rien n'est
// enregistré : c'est une lecture des lignes du devis — sauf le bloc « Délai et dates » (début prévu, personnes prévues, case
// « Afficher le délai dans le PDF client »), enregistré sur la révision en brouillon par le chargé d'affaires (devis.ecrire) ;
// le technicien voit les dates sans pouvoir les modifier. Prix d'achat seulement pour les rôles qui voient les coûts (le serveur
// ne les envoie pas aux autres). Ordre de l'écran : la réponse d'abord (temps et équipe, délai), le détail ensuite.
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarDays, CalendarRange, Clock, FileDown, FileSearch, PackageSearch, Save, Users } from 'lucide-react';
import '../../styles/chantiers.css';
import { api, messageErreur } from '../../lib/api.ts';
import { euros, nombre, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, Chargement, EtatErreur, EtatVide, LienBouton, Panneau, useNotifier } from '../../components/ui.tsx';
import { dateFrCourte } from '@shared/jours-ouvres.ts';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { jourLong } from '../interventions/chantiers/jours.ts';
import type { Effectif } from '../interventions/chantiers/types.ts';
import { useSession } from '../../lib/session.tsx';
import { useModulesEtat } from '../../lib/modules.ts';
import { PanneauRemiseProposee } from './remise-proposee.tsx';

interface EtapeEstimee { label: string; qty_planned: string; unit: string; hours_per_unit: string | null; hours_planned: string | null }
interface PosteEstime {
  label: string; place: 'site' | 'bureau'; crew_min: number | null;
  temps: { heures: string; a_renseigner: number; nb_etapes: number }; etapes: EtapeEstimee[];
}
interface LigneMat {
  cle: string; reference: string | null; label: string; unit: string; quantite: string; quantite_achat: string;
  conditionnement: { quantite: string; libelle: string } | null; nombre_conditionnements: string | null;
  prix_unitaire?: string | null; total_ht?: string | null;
  /** Ligne Ma gamme, compte sans accès achats : prix réservé (`prix_origine` le dit). */
  prix_reserve?: boolean; prix_origine?: string | null;
}
/** Délai d'exécution (server/modules/chantiers/delai.ts). */
interface DelaiDevis {
  saisie: { debut: string | null; personnes: number | null; pdf: boolean };
  calcul: { disponible: false; raison: string } | {
    disponible: true; personnes: number; jours: number; indicatif: boolean; alerte: string | null;
    dates: null | { debut: string; debut_effectif: string; fin: string; feries: { date: string; nom: string }[] };
  };
  imprimable: boolean; motif_non_imprime: string | null;
  /** Absent d'un délai figé avant le 31/10/2026 (France métropolitaine). */
  jours_feries?: { regime: 'metropole' | 'alsace_moselle'; libelle: string; motif: string };
}
interface BlocDelai { fige: boolean; valeur: DelaiDevis | null; ligne_pdf: string | null; pdf_client: boolean; modifiable: boolean; peut_ecrire: boolean }

interface VueEstimation {
  devis: { id: string; number: string; title: string };
  revision: { id: string; rev_no: number; variant: string; status: string };
  nb_lignes: number;
  hypotheses: { heures_sur_site_par_jour: string | null; rendement_pct: string; rendement_saisi: boolean };
  postes: PosteEstime[];
  temps: { site: string; bureau: string; nb_etapes: number; etapes_a_renseigner: number; incomplet: boolean; deplacements: { heures: string; lignes: number; a_renseigner: number } };
  effectif: Effectif;
  delai: BlocDelai;
  non_reprises: { label: string; motif: string }[];
  couts: boolean;
  /** Montant du devis et ce qu'il représente : seulement pour les rôles qui voient les coûts (null sinon). */
  montant: null | {
    complet: boolean; nb_a_renseigner: number; remise_pct: string; taux_tva: string;
    ht_brut: string | null; remise: string | null; ht_net: string | null; tva: string | null; ttc: string | null;
    cout_revient: string | null; marge: string | null; marge_pct: string | null;
    /** Devis avec une ligne Ma gamme, compte sans accès achats : coût et marge totaux réservés ; ceux des autres lignes à part. */
    achats_reserves?: boolean;
    hors_gamme?: { libelle: string; ht: string; cout_revient: string | null; marge: string | null; marge_pct: string | null } | null;
    repartition: { nature: string; libelle: string; ht: string; part_pct: string | null; lignes: number; a_renseigner: number }[];
    par_heure_pose: string | null; heures_incompletes: boolean;
  };
  materiel: {
    fournisseurs: { id: string | null; nom: string; lignes: LigneMat[]; total_ht?: string | null; total_incomplet?: boolean; prix_reserves?: boolean }[];
    totaux: { references: number; total_ht?: string | null; total_incomplet?: boolean; prix_reserves?: boolean };
  };
}

const h = (v: string | null | undefined) => (v === null || v === undefined ? null : `${nombre(v, 2)} h`);
const q = (v: string) => nombre(v, 4);
const JOURS_MAX = 365;
const joursValides = (s: string) => /^\d{1,3}$/.test(s) && Number(s) >= 1 && Number(s) <= JOURS_MAX;

export function PageEstimationPose() {
  const { id = '', quoteId = '' } = useParams();
  const [params] = useSearchParams();
  const rid = params.get('revision') ?? '';
  const [jours, setJours] = useState('');
  const [joursImposes, setJoursImposes] = useState<number | null>(null);
  const r = useQuery({
    queryKey: ['estimation-pose', rid, joursImposes], enabled: !!rid, placeholderData: (p) => p,
    queryFn: () => api.get<VueEstimation>(`/api/devis/revisions/${rid}/estimation-pose${joursImposes ? `?jours=${joursImposes}` : ''}`),
  });
  const retour = `/etudes/${id}/devis/${quoteId}?revision=${rid}`;
  return (
    <DossierCadre dossierId={id} actif="devis" large
      actions={<LienBouton to={retour} icone={<ArrowLeft aria-hidden />}>Retour au devis</LienBouton>}>
      {!rid && <EtatVide titre="Révision non précisée" texte="Ouvrez l’estimation depuis l’écran du devis." />}
      {r.isLoading && <Chargement lignes={6} />}
      {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
      {r.data && <Estimation v={r.data} dossierId={id} retour={retour} jours={jours} setJours={setJours}
        imposer={(n) => setJoursImposes(n)} joursImposes={joursImposes} />}
    </DossierCadre>
  );
}

/** Compte rendu de « Tout préparer » (paramètres de l'adresse, posés par la page Synthèse) : ce qui a été ajouté, ce qui reste. */
function BandeauToutPreparer({ v, dossierId }: { v: VueEstimation; dossierId: string }) {
  const [p] = useSearchParams();
  if (p.get('prepare') !== '1') return null;
  const n = (k: string) => Math.max(0, Number(p.get(k)) || 0);
  const [aj, dj, ac, qs, ec] = ['aj', 'dj', 'ac', 'qs', 'ec'].map(n);
  const revue = `/etudes/${dossierId}/devis/${v.devis.id}/proposition?revision=${v.revision.id}`;
  return (
    <Alerte ton={ac ? 'ambre' : 'succes'} titre={`${v.devis.number} ${p.get('cree') === '1' ? 'créé' : 'repris'} : ${aj ? `${pluriel(aj, 'élément')} ajouté${aj > 1 ? 's' : ''}` : 'rien de nouveau'}${dj ? `, ${dj} déjà au devis` : ''}`}
      action={ac ? <LienBouton compact to={revue} icone={<FileSearch aria-hidden />}>Compléter</LienBouton> : undefined}>
      {ac ? <>Reste à compléter : {ac}{qs ? ` (${qs} sans quantité` : ''}{qs && ec ? ', ' : qs ? ')' : ec ? ' (' : ''}{ec ? `${pluriel(ec, 'écart')} CCTP ↔ DPGF)` : ''}. L’estimation ne compte que ce qui est au devis.</>
        : 'Tout le chiffrage proposé est au devis. Relisez-le avant de l’envoyer.'}
    </Alerte>
  );
}

/**
 * Fin de « Tout préparer » : la remise proposée (dirigeant seul, option Ma gamme active, brouillon). Rien n'est appliqué d'office :
 * le dirigeant choisit un taux, ou n'en choisit aucun. L'estimation est recalculée après un choix.
 */
function RemiseFinToutPreparer({ v }: { v: VueEstimation }) {
  const [p] = useSearchParams();
  const { peut } = useSession();
  const { modules } = useModulesEtat();
  const qc = useQueryClient();
  if (p.get('prepare') !== '1' || v.revision.status !== 'brouillon' || !peut('rentabilite.lire') || !modules.ma_gamme) return null;
  return <PanneauRemiseProposee revisionId={v.revision.id} apres={() => qc.invalidateQueries({ queryKey: ['estimation-pose', v.revision.id] })} />;
}

/** Le montant du devis et ce qu'il représente : HT, TVA, TTC, répartition par nature, coût et marge, prix par heure de pose. */
function PanneauMontant({ m }: { m: NonNullable<VueEstimation['montant']> }) {
  return (
    <Panneau titre="Montant du devis">
      <div className="pile" style={{ gap: 'var(--e-3)' }}>
        {!m.complet && (
          <Alerte ton="ambre" titre={`Montant partiel : ${pluriel(m.nb_a_renseigner, 'ligne')} sans prix`}>
            Les montants ne comptent que les lignes chiffrées. Renseignez les prix manquants dans le devis.
          </Alerte>
        )}
        <dl className="ch-chiffres">
          <div><dt>Total HT{m.remise && Number(m.remise) > 0 ? ' (après remise)' : ''}</dt><dd className="chiffre">{euros(m.ht_net)}</dd></div>
          <div><dt>TVA {nombre(m.taux_tva, 2)} %</dt><dd className="chiffre">{euros(m.tva)}</dd></div>
          <div><dt>Total TTC</dt><dd className="chiffre">{euros(m.ttc)}</dd></div>
          {m.achats_reserves && m.hors_gamme?.marge != null && <div><dt>Marge {m.hors_gamme.libelle}</dt><dd className="chiffre">{euros(m.hors_gamme.marge)}{m.hors_gamme.marge_pct ? <span className="petit discret"> ({nombre(m.hors_gamme.marge_pct, 1)} %)</span> : null}</dd></div>}
          {m.marge !== null && <div><dt>Marge</dt><dd className="chiffre">{euros(m.marge)}{m.marge_pct ? <span className="petit discret"> ({nombre(m.marge_pct, 1)} %)</span> : null}</dd></div>}
        </dl>
        <div>
          <p className="champ-libelle" style={{ margin: '0 0 var(--e-2)' }}>Ce que représente le montant (HT, avant remise)</p>
          <ul className="pile" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 'var(--e-3)' }}>
            {m.repartition.map((r) => (
              <li key={r.nature} className="pile" style={{ gap: 4, minWidth: 0 }}>
                <span className="rangee" style={{ justifyContent: 'space-between', gap: 'var(--e-3)', flexWrap: 'wrap' }}>
                  <span style={{ overflowWrap: 'anywhere', minWidth: 0, flex: '1 1 200px' }}>{r.libelle}</span>
                  <strong className="chiffre">{euros(r.ht)}{r.part_pct !== null ? <span className="petit discret"> — {nombre(r.part_pct, 1)} %</span> : null}</strong>
                </span>
                <span aria-hidden style={{ display: 'block', height: 8, borderRadius: 4, background: 'var(--trait)' }}>
                  <span style={{ display: 'block', height: 8, borderRadius: 4, width: `${Math.min(100, Number(r.part_pct ?? 0))}%`, background: 'var(--accent)' }} />
                </span>
                {r.a_renseigner > 0 && <span className="tres-petit discret">{pluriel(r.a_renseigner, 'ligne')} sans prix, non comptée{r.a_renseigner > 1 ? 's' : ''}</span>}
              </li>
            ))}
          </ul>
        </div>
        <p className="tres-petit discret" style={{ margin: 0 }}>
          {m.cout_revient !== null ? <>Coût de revient : {euros(m.cout_revient)} HT. </> : null}
          {m.achats_reserves ? <>Coût de revient et marge du devis réservés : il contient des produits Ma gamme (accès achats){m.hors_gamme?.cout_revient != null ? <> ; coût de revient {m.hors_gamme.libelle} : {euros(m.hors_gamme.cout_revient)} HT</> : null}. </> : null}
          {m.par_heure_pose !== null ? <>Rapporté aux heures de pose prévues : {euros(m.par_heure_pose)} HT par heure{m.heures_incompletes ? ' (heures incomplètes)' : ''}, matériel compris. </> : null}
          Mêmes chiffres que l’écran du devis ; visibles seulement par les rôles qui voient les coûts.
        </p>
      </div>
    </Panneau>
  );
}

const PERSONNES_MAX = 50;
const personnesValides = (s: string) => s === '' || (/^\d{1,2}$/.test(s) && Number(s) >= 1 && Number(s) <= PERSONNES_MAX);
const texteSaisie = (n: number | null | undefined) => (n ? String(n) : '');

/**
 * Délai et dates : saisie (début prévu, personnes prévues, case PDF) sur un brouillon, puis le délai calculé par le serveur —
 * jours ouvrés, dates, jours fériés rencontrés — et ce qui sera imprimé (ou pourquoi rien ne l'est) dans le PDF client.
 */
function PanneauDelai({ b, rid, statut }: { b: BlocDelai; rid: string; statut: string }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const s = b.valeur?.saisie ?? null;
  const [debut, setDebut] = useState(s?.debut ?? '');
  const [personnes, setPersonnes] = useState(texteSaisie(s?.personnes));
  const [pdf, setPdf] = useState(s?.pdf ?? false);
  // Reprendre la saisie quand le serveur en renvoie une autre (après enregistrement, autre révision).
  useEffect(() => { setDebut(s?.debut ?? ''); setPersonnes(texteSaisie(s?.personnes)); setPdf(s?.pdf ?? false); }, [s?.debut, s?.personnes, s?.pdf, rid]);
  const modifie = !!s && (debut !== (s.debut ?? '') || personnes !== texteSaisie(s.personnes) || pdf !== s.pdf);
  const enregistrer = useMutation({
    mutationFn: () => api.patch(`/api/devis/revisions/${rid}`, { plannedStart: debut || null, plannedCrew: personnes ? Number(personnes) : null, delayOnPdf: pdf }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ['estimation-pose', rid] }), qc.invalidateQueries({ queryKey: ['revision', rid] })]);
      notifier('Délai et dates enregistrés.');
    },
    onError: (e) => notifier(messageErreur(e), true),
  });
  const c = b.valeur?.calcul ?? null;
  const etat = b.fige ? 'Révision émise : délai figé à l’émission, il ne change plus.'
    : !b.peut_ecrire ? 'Consultation : seul le chargé d’affaires modifie ces dates.'
      : statut !== 'brouillon' ? 'Révision en cours de validation : repassez-la en brouillon pour modifier ces dates.' : null;
  return (
    <Panneau titre="Délai et dates">
      <div className="pile" style={{ gap: 'var(--e-3)' }}>
        {b.fige && !b.valeur && (
          <EtatVide icone={<CalendarRange />} titre="Aucun délai figé"
            texte="Cette révision a été émise avant l’ajout du délai d’exécution : aucun délai n’a été figé. Créez une nouvelle révision pour en annoncer un." />
        )}
        {b.modifiable ? (
          <form className="ch-delai" onSubmit={(ev) => { ev.preventDefault(); if (personnesValides(personnes)) enregistrer.mutate(); }}>
            <Champ libelle="Date de début prévue" type="date" value={debut} min="2000-01-01" max="2199-12-31" onChange={(ev) => setDebut(ev.target.value)}
              aide="Facultative : sans elle, le délai est donné sans dates." />
            <Champ libelle={`Personnes prévues (1 à ${PERSONNES_MAX})`} inputMode="numeric" pattern="[0-9]*" value={personnes} placeholder="Ex. 2"
              onChange={(ev) => setPersonnes(ev.target.value.replace(/\D/g, '').slice(0, 2))}
              erreur={personnesValides(personnes) ? null : `Un nombre entier de 1 à ${PERSONNES_MAX}.`} />
            <label className="case ch-delai-case"><input type="checkbox" checked={pdf} onChange={(ev) => setPdf(ev.target.checked)} /> Afficher le délai dans le PDF client</label>
            <Bouton type="submit" variante="principal" icone={<Save aria-hidden />} chargement={enregistrer.isPending}
              disabled={!modifie || !personnesValides(personnes)}>Enregistrer</Bouton>
          </form>
        ) : s && (
          <dl className="ch-chiffres">
            <div><dt>Début prévu</dt><dd>{s.debut ? dateFrCourte(s.debut) : 'Non saisi'}</dd></div>
            <div><dt>Personnes prévues</dt><dd>{s.personnes ?? 'Non saisi'}</dd></div>
            <div><dt>PDF client</dt><dd>{s.pdf ? 'Délai affiché' : 'Délai non affiché'}</dd></div>
          </dl>
        )}
        {etat && <p className="petit secondaire" style={{ margin: 0 }}>{etat}</p>}
        {c && !c.disponible && <Alerte ton="ambre" titre="Pas de délai calculé">{c.raison}</Alerte>}
        {c && c.disponible && (
          <div className="pile" style={{ gap: 'var(--e-2)' }}>
            <p className="ch-retenu" style={{ margin: 0 }}>
              <CalendarDays aria-hidden />
              <span style={{ flex: '1 1 12rem', minWidth: 0 }}><strong>{pluriel(c.jours, 'jour ouvré', 'jours ouvrés')} avec {pluriel(c.personnes, 'personne')}</strong>
                {c.dates ? <> : du <strong>{dateFrCourte(c.dates.debut_effectif)}</strong> au <strong>{dateFrCourte(c.dates.fin)}</strong></> : null}</span>
              {c.indicatif && <Badge ton="ambre">indicatif (temps incomplet)</Badge>}
            </p>
            {!c.dates && <p className="petit secondaire" style={{ margin: 0 }}>Saisissez la date de début pour obtenir la date de fin prévue.</p>}
            {c.dates && c.dates.debut_effectif !== c.dates.debut && (
              <p className="petit secondaire" style={{ margin: 0 }}>Le {dateFrCourte(c.dates.debut)} n’est pas un jour ouvré : le travail commence le {dateFrCourte(c.dates.debut_effectif)}.</p>
            )}
            {c.dates && (
              <p className="petit" style={{ margin: 0 }}>
                {c.dates.feries.length
                  ? <>Jours fériés rencontrés, non travaillés : {c.dates.feries.map((f) => `${jourLong(f.date)} (${f.nom})`).join(', ')}.</>
                  : 'Aucun jour férié sur la période.'}
              </p>
            )}
            {c.indicatif && <Alerte ton="ambre">Le temps de certaines étapes est à renseigner : ce délai ne compte que les heures connues.</Alerte>}
            {c.alerte && <Alerte ton="ambre">{c.alerte}</Alerte>}
          </div>
        )}
        {b.valeur && b.valeur.saisie.pdf && (b.ligne_pdf
          ? <Alerte ton="succes" titre="Imprimé dans le PDF client">
              <span className="pile" style={{ gap: 'var(--e-2)', alignItems: 'flex-start' }}>
                <span>« {b.ligne_pdf} »</span>
                {b.pdf_client && <a className="btn btn-compact" href={`/api/devis/revisions/${rid}/client.pdf`} target="_blank" rel="noopener"><FileDown aria-hidden />Ouvrir le PDF client</a>}
              </span>
            </Alerte>
          : <Alerte ton="ambre" titre="Rien n’est imprimé dans le PDF client">{b.valeur.motif_non_imprime}</Alerte>)}
        <p className="tres-petit discret" style={{ margin: 0 }}>
          Jours ouvrés : du lundi au vendredi, hors jours fériés {b.valeur?.jours_feries ? `(${b.valeur.jours_feries.libelle})` : 'de France métropolitaine'}, sans congés ni intempéries. {b.valeur?.jours_feries?.motif ?? ''}
          Même temps sur site et mêmes hypothèses de l’entreprise que le tableau ci-dessus. Le PDF client ne montre que le nombre de jours et les dates.
        </p>
      </div>
    </Panneau>
  );
}

function Estimation({ v, dossierId, retour, jours, setJours, imposer, joursImposes }: {
  v: VueEstimation; dossierId: string; retour: string; jours: string; setJours: (s: string) => void; imposer: (n: number | null) => void; joursImposes: number | null;
}) {
  const t = v.temps;
  const e = v.effectif;
  const brouillon = v.revision.status === 'brouillon';
  if (!v.nb_lignes) {
    return (
      <Panneau titre="Estimation de pose">
        <EtatVide icone={<FileSearch />} titre="Le devis est vide"
          texte="Ajoutez d’abord les postes du devis (chiffrage proposé depuis le CCTP et le DPGF, ouvrages types, catalogue) : l’estimation se calcule à partir de ses lignes."
          action={<LienBouton to={`/etudes/${dossierId}/devis/${v.devis.id}/proposition?revision=${v.revision.id}`} icone={<FileSearch aria-hidden />}>Chiffrage proposé (CCTP, DPGF)</LienBouton>} />
      </Panneau>
    );
  }
  return (
    <div className="pile-l">
      <BandeauToutPreparer v={v} dossierId={dossierId} />
      <RemiseFinToutPreparer v={v} />
      <div className="pile" style={{ gap: 4 }}>
        <h2 style={{ margin: 0 }}>Estimation de pose</h2>
        <p className="petit secondaire" style={{ margin: 0 }}>
          <span className="mono">{v.devis.number}</span> {v.devis.title} — {v.revision.variant}, révision {v.revision.rev_no}
          {brouillon ? ' (brouillon : l’estimation suit vos modifications)' : ''}
        </p>
      </div>

      {/* ── La réponse : combien de temps, combien de personnes ── */}
      <Panneau titre="Temps et équipe">
        <div className="pile" style={{ gap: 'var(--e-3)' }}>
          <dl className="ch-chiffres">
            <div><dt><Clock aria-hidden /> Sur site</dt><dd className="chiffre">{h(t.site)}</dd></div>
            <div><dt>Au bureau</dt><dd className="chiffre">{h(t.bureau)}</dd></div>
            <div><dt>Étapes</dt><dd className="chiffre">{t.nb_etapes}</dd></div>
            {e.disponible && <div><dt><CalendarDays aria-hidden /> Jours-personne</dt><dd className="chiffre">{nombre(e.jours_personne, 2)}</dd></div>}
            {t.deplacements.lignes > 0 && <div><dt>Déplacements</dt><dd className="chiffre">{h(t.deplacements.heures)}{t.deplacements.a_renseigner ? ' + à renseigner' : ''}</dd></div>}
          </dl>
          {t.incomplet && (
            <Alerte ton="ambre" titre={`Temps incomplet : ${pluriel(t.etapes_a_renseigner, 'étape')} sans temps prévu`}>
              Le total ne compte que les heures connues. Renseignez le temps de ces lignes dans le devis.
            </Alerte>
          )}
          {!e.disponible && <Alerte ton="ambre" titre="Combien de personnes, combien de jours">{e.raison}</Alerte>}
          {e.disponible && (
            <>
              <div>
                <p className="champ-libelle" style={{ margin: '0 0 var(--e-2)' }}>Combien de personnes, combien de jours</p>
                <ul className="ch-tableau-effectif" style={{ listStyle: 'none' }}>
                  {e.lignes.map((l) => (
                    <li key={l.personnes} className={`ch-choix ${e.retenu?.personnes === l.personnes ? 'est-choisi' : ''}`} style={{ cursor: 'default' }}>
                      <Users aria-hidden style={{ width: 18, height: 18 }} />
                      <span><strong>{pluriel(l.personnes, 'personne')}</strong> → {pluriel(l.jours, 'jour')}</span>
                    </li>
                  ))}
                </ul>
                {e.max_utile_non_precise && <p className="tres-petit discret" style={{ margin: 'var(--e-2) 0 0' }}>Au moins un poste ne précise pas son nombre minimum de personnes : il est compté pour une.</p>}
              </div>
              <form className="ch-jours" onSubmit={(ev) => { ev.preventDefault(); if (joursValides(jours)) imposer(Number(jours)); }}>
                <label className="champ">
                  <span className="champ-libelle">Date imposée : nombre de jours de travail (1 à {JOURS_MAX})</span>
                  <input className="saisie" inputMode="numeric" pattern="[0-9]*" value={jours} onChange={(ev) => setJours(ev.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="Ex. 5" />
                </label>
                <Bouton type="submit" disabled={!joursValides(jours)}>Calculer l’effectif</Bouton>
                {joursImposes !== null && <Bouton variante="fantome" onClick={() => { setJours(''); imposer(null); }}>Effacer</Bouton>}
              </form>
              {e.retenu && (
                <div className="pile" style={{ gap: 'var(--e-2)' }}>
                  <p className="ch-retenu" style={{ margin: 0 }}>
                    <CalendarDays aria-hidden /> <strong>{pluriel(e.retenu.jours, 'jour')}</strong> <span aria-hidden>→</span>
                    <Users aria-hidden /> <strong>{pluriel(e.retenu.personnes, 'personne')} nécessaire{e.retenu.personnes > 1 ? 's' : ''}</strong>
                  </p>
                  {e.retenu.alerte && <Alerte ton="ambre">{e.retenu.alerte}</Alerte>}
                </div>
              )}
              <p className="tres-petit discret" style={{ margin: 0 }}>
                Hypothèses de l’entreprise (Administration › Chantiers) : {h(v.hypotheses.heures_sur_site_par_jour)} sur site par jour et par personne,
                rendement {nombre(v.hypotheses.rendement_pct, 2)} %{v.hypotheses.rendement_saisi ? '' : ' (non saisi : aucune correction)'}. Jours de travail arrondis au jour supérieur, sans jours fériés ni congés.
              </p>
            </>
          )}
        </div>
      </Panneau>

      <PanneauDelai b={v.delai} rid={v.revision.id} statut={v.revision.status} />

      {v.montant && <PanneauMontant m={v.montant} />}

      {/* ── Le matériel ── */}
      <Panneau serre titre="Matériel à commander">
        <p className="petit secondaire ch-mat-resume">
          {pluriel(v.materiel.totaux.references, 'référence')}
          {v.couts && v.materiel.totaux.prix_reserves && v.materiel.fournisseurs.every((g) => g.lignes.every((l) => l.prix_reserve)) ? <> — fournitures : prix réservé (produits Ma gamme, accès achats)</>
            : v.couts && v.materiel.totaux.total_ht !== undefined ? <> — fournitures : <strong className="chiffre">{euros(v.materiel.totaux.total_ht)}</strong>{v.materiel.totaux.total_incomplet ? ' HT, total incomplet (prix à renseigner)' : ' HT'}{v.materiel.totaux.prix_reserves ? ', hors produits Ma gamme (prix réservé)' : ''}</> : null}
        </p>
        {!v.materiel.totaux.references && <EtatVide icone={<PackageSearch />} titre="Aucune fourniture" texte="Ce devis ne contient que de la main-d’œuvre, des frais ou de la sous-traitance." />}
        {v.materiel.fournisseurs.map((g) => (
          <section key={g.id ?? 'a-choisir'} className="ch-mat-groupe" aria-label={g.nom}>
            <h3 className="ch-mat-titre">
              <span>{g.nom}</span>
              <span className="petit discret">{pluriel(g.lignes.length, 'référence')}{v.couts && g.total_ht !== undefined && !(g.prix_reserves && g.lignes.every((l) => l.prix_reserve)) ? ` — ${euros(g.total_ht)} HT${g.total_incomplet ? ' (incomplet)' : ''}${g.prix_reserves ? ' hors Ma gamme' : ''}` : ''}</span>
            </h3>
            <ul className="ch-mat-cartes">
              {g.lignes.map((l) => (
                <li key={l.cle} className="ch-mat-carte">
                  <span className="ch-mat-libelle">{l.label}</span>
                  {l.reference && <span className="mono petit">{l.reference}</span>}
                  <span className="petit"><strong className="chiffre">{q(l.quantite)} {l.unit}</strong>
                    {l.conditionnement ? ` — à acheter : ${l.nombre_conditionnements} × ${l.conditionnement.libelle} (${q(l.quantite_achat)} ${l.unit})` : ''}</span>
                  {v.couts && l.prix_reserve && <span className="petit discret">{l.prix_origine ?? 'Prix réservé (accès achats)'}</span>}
                  {v.couts && !l.prix_reserve && l.total_ht !== undefined && <span className="petit secondaire chiffre">{l.prix_unitaire ? `${euros(l.prix_unitaire)} HT / ${l.unit} — ${euros(l.total_ht)}` : 'Prix à renseigner'}</span>}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </Panneau>

      {/* ── Le détail de la pose ── */}
      <Panneau titre={`Postes et étapes (${v.postes.length})`}>
        <ol className="ch-postes">
          {v.postes.map((p, i) => (
            <li key={`${p.label}-${i}`} className="ch-poste">
              <div className="ch-poste-entete">
                <span className="ch-poste-num" aria-hidden>{i + 1}</span>
                <div className="pile" style={{ gap: 2, minWidth: 0 }}>
                  <h3 className="ch-poste-titre">{p.label}</h3>
                  <span className="petit secondaire">
                    {p.place === 'bureau' ? 'Au bureau' : 'Sur site'} — {h(p.temps.heures)}{p.temps.a_renseigner ? ` + ${pluriel(p.temps.a_renseigner, 'étape')} à renseigner` : ''}
                    {p.crew_min && p.crew_min > 1 ? ` — ${p.crew_min} personnes minimum` : ''}
                  </span>
                </div>
              </div>
              <ul className="ch-etapes">
                {p.etapes.map((s, j) => (
                  <li key={j} className="ch-etape">
                    <span className="ch-etape-texte"><span className="ch-etape-label">{s.label}</span></span>
                    {s.hours_planned === null ? <Badge ton="ambre">Temps à renseigner</Badge>
                      : <span className="chiffre petit">{q(s.qty_planned)} {s.unit}<span className="discret"> × {nombre(s.hours_per_unit, 4)} h</span> = <strong>{h(s.hours_planned)}</strong></span>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        {v.non_reprises.length > 0 && (
          <p className="tres-petit discret" style={{ margin: 'var(--e-3) 0 0' }}>
            Non repris dans la pose : {v.non_reprises.map((n) => `${n.label} (${n.motif.toLowerCase()})`).join(' ; ')}.
          </p>
        )}
      </Panneau>

      <Alerte titre="Comment l’estimation est faite">
        Le même calcul que « Préparer le chantier », appliqué aux lignes de ce devis avant son acceptation : un ouvrage type = un poste, ses
        composants qui portent des heures = ses étapes. Les temps des ouvrages types fournis au départ sont des estimations à valider avec vos
        équipes. Rien n’est enregistré, sauf le bloc « Délai et dates » ; le chantier ne sera créé qu’après l’acceptation du devis.
        <span> <LienBouton compact to={retour} icone={<ArrowLeft aria-hidden />}>Retour au devis</LienBouton></span>
      </Alerte>
    </div>
  );
}
